import React, { useState, useEffect } from 'react';
import { useManagerAuth } from '../../context/ManagerAuthContext';
import {
  ShoppingCart, Search, ChevronDown, ChevronUp, Package, X, Check, RefreshCw,
  Clock, CheckCircle2, Truck, XCircle, AlertCircle, Eye, Edit3, DollarSign,
  User, MapPin, Phone, Mail, CreditCard, Building2, Sparkles, Send, FileText,
  ChevronRight, ArrowRight
} from 'lucide-react';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4002';

const STATUS_COLORS = {
  pending:    'bg-amber-100 text-amber-800 border-amber-300',
  paid:       'bg-blue-100 text-blue-800 border-blue-300',
  confirmed:  'bg-blue-100 text-blue-800 border-blue-300',
  processing: 'bg-indigo-100 text-indigo-800 border-indigo-300',
  shipped:    'bg-purple-100 text-purple-800 border-purple-300',
  delivered:  'bg-emerald-100 text-emerald-800 border-emerald-300',
  cancelled:  'bg-rose-100 text-rose-800 border-rose-300',
};

const ManagerOrders = () => {
  const { getToken } = useManagerAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expanded, setExpanded] = useState(null);
  const [metrics, setMetrics] = useState({
    all: 0, pending: 0, paid: 0, processing: 0, shipped: 0, delivered: 0, cancelled: 0, totalRevenue: 0
  });

  // Modal State
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);

  // Edit controls inside modal
  const [editStatus, setEditStatus] = useState('');
  const [editCourier, setEditCourier] = useState('');
  const [editTracking, setEditTracking] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [toast, setToast] = useState('');

  useEffect(() => {
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus]);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 3500);
  };

  const fetchOrders = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filterStatus !== 'all') p.set('status', filterStatus);
    if (search.trim()) p.set('search', search.trim());

    try {
      const r = await fetch(`${API}/api/manager/orders?${p}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      if (d.success) {
        setOrders(d.orders || []);
        setTotal(d.total || 0);
        if (d.metrics) setMetrics(d.metrics);
      }
    } catch (e) {
      console.error('❌ Error fetching manager orders:', e);
      showToast('Failed to load warehouse orders');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenOrderModal = async (orderId) => {
    try {
      setIsModalOpen(true);
      setModalLoading(true);
      const r = await fetch(`${API}/api/manager/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      const d = await r.json();
      if (d.success && d.order) {
        const ord = d.order;
        setSelectedOrder(ord);
        setEditStatus(ord.status || 'pending');
        setEditCourier(ord.courier_name || '');
        setEditTracking(ord.tracking_number || '');
        setEditNotes(ord.admin_notes || '');
      } else {
        showToast(d.message || 'Order not found');
        setIsModalOpen(false);
      }
    } catch (e) {
      console.error(e);
      showToast('Failed to fetch order details');
      setIsModalOpen(false);
    } finally {
      setModalLoading(false);
    }
  };

  const handleSaveUpdate = async () => {
    if (!selectedOrder) return;
    setUpdating(true);
    try {
      const r = await fetch(`${API}/api/manager/orders/${selectedOrder.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getToken()}`
        },
        body: JSON.stringify({
          status: editStatus,
          courier_name: editCourier,
          tracking_number: editTracking,
          admin_notes: editNotes
        }),
      });
      const d = await r.json();
      if (d.success) {
        showToast(`Order #${selectedOrder.order_number || selectedOrder.id.slice(0, 8)} updated successfully!`);
        setSelectedOrder(d.order || { ...selectedOrder, status: editStatus, courier_name: editCourier, tracking_number: editTracking, admin_notes: editNotes });
        fetchOrders();
      } else {
        alert(d.message || 'Failed to update order');
      }
    } catch (e) {
      alert(e.message || 'Failed to update order');
    } finally {
      setUpdating(false);
    }
  };

  const formatINR = (amt) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(amt || 0);
  };

  return (
    <div className="space-y-6 select-none font-sans pb-12 relative">
      
      {/* Toast Notification */}
      {toast && (
        <div className="fixed top-5 right-5 z-50 bg-slate-900 text-cyan-400 border border-cyan-500/50 px-5 py-3.5 rounded-2xl shadow-2xl flex items-center gap-2.5 text-xs font-black uppercase tracking-wider animate-fade-in-down">
          <CheckCircle2 className="w-4 h-4 text-cyan-400" />
          <span>{toast}</span>
        </div>
      )}

      {/* ── 1. ROYAL BLUE CYAN HERO HEADER ── */}
      <div className="relative bg-gradient-to-r from-blue-950 via-slate-900 to-cyan-950 rounded-3xl p-6 sm:p-8 text-white border border-blue-800/60 shadow-2xl overflow-hidden group">
        
        {/* Soft Ambient Radial Lights */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-br from-blue-500/20 via-cyan-500/10 to-transparent rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-6">
          <div className="space-y-2">
            <div className="flex items-center gap-2.5">
              <span className="px-3 py-1 rounded-full bg-blue-500/20 text-cyan-300 border border-blue-500/40 text-[10px] font-black uppercase tracking-widest flex items-center gap-1.5 shadow-xs">
                <Truck className="w-3.5 h-3.5 text-cyan-400" />
                FULFILLMENT DISPATCH PIPELINE
              </span>
              <span className="text-xs text-cyan-300 font-mono font-bold">
                ● COURIER INTEGRATION ACTIVE
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl font-black tracking-tight uppercase italic text-white flex items-center gap-3">
              DISPATCH & ORDERS RADAR 🚚
            </h1>

            <p className="text-xs sm:text-sm text-slate-300 font-medium max-w-xl">
              Inspect incoming fulfillment orders, assign courier partners (Delhivery, Blue Dart), log AWB tracking numbers, and manage delivery status.
            </p>
          </div>

          <div className="flex items-center gap-3 shrink-0">
            <button
              onClick={fetchOrders}
              disabled={loading}
              className="p-3 bg-slate-800/80 hover:bg-slate-700 border border-slate-600 rounded-2xl text-slate-300 hover:text-white transition-all shadow-md active:scale-95 disabled:opacity-50"
              title="Refresh Orders"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin text-cyan-400' : ''}`} />
            </button>

            <button
              onClick={fetchOrders}
              className="px-5 py-3 bg-gradient-to-r from-blue-600 via-cyan-600 to-blue-500 hover:from-blue-500 hover:to-cyan-500 text-white text-xs font-black uppercase tracking-wider rounded-2xl flex items-center gap-2 shadow-lg shadow-blue-600/30 transition-all hover:scale-105 active:scale-95"
            >
              <ShoppingCart className="w-4 h-4" />
              <span>Reload Pipeline</span>
            </button>
          </div>
        </div>
      </div>

      {/* ── 2. METRICS TELEMETRY STRIP ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        
        {/* Metric 1 */}
        <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-slate-900 font-mono">{metrics.all}</p>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Total Orders</p>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border border-slate-200">
            ALL
          </span>
        </div>

        {/* Metric 2 */}
        <div className="bg-white rounded-2xl p-4 border border-amber-200 bg-amber-50/30 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-amber-900 font-mono">{metrics.pending + metrics.paid}</p>
            <p className="text-[10px] font-black text-amber-700 uppercase tracking-wider">Pending Queue</p>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
            ACTION
          </span>
        </div>

        {/* Metric 3 */}
        <div className="bg-white rounded-2xl p-4 border border-indigo-200 bg-indigo-50/30 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-indigo-900 font-mono">{metrics.processing}</p>
            <p className="text-[10px] font-black text-indigo-700 uppercase tracking-wider">In Packing</p>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-800 border border-indigo-300">
            PACKING
          </span>
        </div>

        {/* Metric 4 */}
        <div className="bg-white rounded-2xl p-4 border border-purple-200 bg-purple-50/30 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-purple-900 font-mono">{metrics.shipped}</p>
            <p className="text-[10px] font-black text-purple-700 uppercase tracking-wider">Dispatched</p>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-purple-100 text-purple-800 border border-purple-300">
            IN TRANSIT
          </span>
        </div>

        {/* Metric 5 */}
        <div className="col-span-2 lg:col-span-1 bg-white rounded-2xl p-4 border border-emerald-200 bg-emerald-50/30 shadow-sm flex items-center justify-between">
          <div>
            <p className="text-2xl font-black text-emerald-900 font-mono">{formatINR(metrics.totalRevenue)}</p>
            <p className="text-[10px] font-black text-emerald-700 uppercase tracking-wider">Pipeline Value</p>
          </div>
          <span className="text-[10px] font-black uppercase px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-300">
            VALUE
          </span>
        </div>

      </div>

      {/* ── 3. SEARCH & STATUS FILTER TOOLBAR ── */}
      <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm space-y-4">
        
        {/* Status Pill Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-1 border-b border-slate-100 no-scrollbar">
          {[
            { key: 'all', label: 'All Orders', count: metrics.all },
            { key: 'pending', label: 'Pending', count: metrics.pending },
            { key: 'paid', label: 'Paid', count: metrics.paid },
            { key: 'processing', label: 'Processing', count: metrics.processing },
            { key: 'shipped', label: 'Shipped', count: metrics.shipped },
            { key: 'delivered', label: 'Delivered', count: metrics.delivered },
            { key: 'cancelled', label: 'Cancelled', count: metrics.cancelled },
          ].map(tab => {
            const active = filterStatus === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setFilterStatus(tab.key)}
                className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all whitespace-nowrap ${
                  active 
                    ? 'bg-blue-600 text-white shadow-md shadow-blue-600/20' 
                    : 'bg-slate-50 hover:bg-slate-100 text-slate-600 border border-slate-200/80'
                }`}
              >
                <span>{tab.label}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-mono font-bold ${
                  active ? 'bg-blue-800 text-white' : 'bg-slate-200 text-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Search Bar */}
        <div className="flex gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchOrders()}
              placeholder="Search by order #, customer name, tracking AWB..."
              className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-xs text-slate-900 font-medium placeholder-slate-400 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all"
            />
          </div>

          <button
            onClick={fetchOrders}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-transform active:scale-95 shadow-md shadow-blue-600/20"
          >
            Search Orders
          </button>
        </div>

      </div>

      {/* ── 4. DISPATCH ORDERS CARDS LIST ── */}
      {loading ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-16 text-center space-y-3">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin mx-auto" />
          <p className="text-xs font-black text-slate-400 uppercase tracking-widest">Loading Warehouse Dispatch Queue...</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="bg-white rounded-3xl border border-slate-200 p-16 text-center space-y-4">
          <ShoppingCart className="w-14 h-14 text-slate-300 mx-auto" />
          <div className="space-y-1">
            <h3 className="text-base font-black uppercase text-slate-800">No Orders In This Filter</h3>
            <p className="text-xs font-medium text-slate-400">Try switching status tabs or clearing your search term.</p>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map(order => {
            const isExpanded = expanded === order.id;
            return (
              <div
                key={order.id}
                className="bg-white rounded-3xl border border-slate-200 overflow-hidden hover:border-blue-400 transition-all duration-300 shadow-sm hover:shadow-xl group"
              >
                {/* Main Card Summary Bar */}
                <div
                  onClick={() => setExpanded(isExpanded ? null : order.id)}
                  className="p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 cursor-pointer hover:bg-blue-50/20 transition-colors"
                >
                  {/* Left Specs */}
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-2xl bg-blue-50 border border-blue-200 flex items-center justify-center text-blue-700 font-mono font-black text-xs shrink-0 group-hover:scale-105 transition-transform">
                      #{order.order_number ? String(order.order_number).slice(-4) : order.id.slice(0, 4)}
                    </div>

                    <div className="space-y-1">
                      <div className="flex items-center gap-2.5 flex-wrap">
                        <span className="font-black text-slate-900 text-sm font-mono uppercase">
                          #{order.order_number || order.id.slice(0, 8)}
                        </span>
                        <span className={`text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border ${STATUS_COLORS[order.status?.toLowerCase()] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                          {order.status}
                        </span>
                        {order.courier_name && (
                          <span className="text-[10px] font-bold px-2.5 py-0.5 bg-slate-100 text-slate-700 rounded-md border border-slate-200 flex items-center gap-1">
                            <Truck className="w-3 h-3 text-blue-600" />
                            {order.courier_name} {order.tracking_number ? `(${order.tracking_number})` : ''}
                          </span>
                        )}
                      </div>

                      <p className="text-xs text-slate-500 font-medium">
                        Customer: <strong className="text-slate-800">{order.customer?.name || order.shipping_address?.full_name || 'Direct Buyer'}</strong> • {new Date(order.created_at).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* Right Price & Actions */}
                  <div className="flex items-center justify-between md:justify-end gap-6 pt-3 md:pt-0 border-t md:border-t-0 border-slate-100">
                    <div className="text-left md:text-right">
                      <p className="font-black text-slate-900 font-mono text-base">{formatINR(order.total_amount)}</p>
                      <p className="text-[11px] text-slate-400 font-bold uppercase tracking-wider font-mono">
                        {(order.items || []).length} ITEM(S)
                      </p>
                    </div>

                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleOpenOrderModal(order.id)}
                        className="px-4 py-2 bg-slate-900 hover:bg-blue-600 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md flex items-center gap-1.5 hover:scale-105 active:scale-95"
                      >
                        <Eye className="w-4 h-4" />
                        <span>Manage Order</span>
                      </button>
                      
                      <button 
                        onClick={() => setExpanded(isExpanded ? null : order.id)}
                        className="p-2 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
                      >
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>

                </div>

                {/* Expanded Quick Inspector */}
                {isExpanded && (
                  <div className="border-t border-slate-100 p-5 bg-slate-50/80 space-y-4">
                    <div>
                      <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3">
                        ORDER ITEM SPECIFICATIONS
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {(order.items || []).map(item => (
                          <div key={item.id} className="flex items-center gap-3 bg-white rounded-2xl p-3 border border-slate-200/80 shadow-2xs">
                            <div className="w-10 h-10 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                              {item.product?.image_url ? (
                                <img src={item.product.image_url} alt="" className="w-full h-full object-contain" />
                              ) : (
                                <Package className="w-5 h-5 text-slate-300" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-black text-slate-900 uppercase truncate">
                                {item.product_name || item.product?.name}
                              </p>
                              <p className="text-[10px] font-mono text-slate-500">
                                Qty: {item.quantity} × {formatINR(item.unit_price)}
                              </p>
                            </div>
                            <span className="text-xs font-black font-mono text-slate-900">
                              {formatINR(item.total_price || (item.quantity * item.unit_price))}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      <div className="bg-white rounded-2xl p-4 border border-slate-200/80 space-y-1 text-xs">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Customer Profile</p>
                        <p className="font-bold text-slate-900">{order.customer?.name || order.shipping_address?.full_name}</p>
                        <p className="text-slate-500 font-mono">{order.customer?.email}</p>
                        {order.customer?.phone && <p className="text-slate-500 font-mono">Phone: {order.customer.phone}</p>}
                      </div>

                      {order.shipping_address && (
                        <div className="bg-white rounded-2xl p-4 border border-slate-200/80 space-y-1 text-xs">
                          <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider mb-1">Destination Address</p>
                          <p className="font-bold text-slate-900">{order.shipping_address.full_name}</p>
                          <p className="text-slate-600">{order.shipping_address.address_line1}</p>
                          <p className="text-slate-600">{order.shipping_address.city}, {order.shipping_address.state} - {order.shipping_address.pincode}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

              </div>
            );
          })}
        </div>
      )}

      {/* ── 5. FULL-SCREEN DISPATCH & COURIER CONTROL MODAL ── */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/70 backdrop-blur-md animate-fade-in overflow-y-auto">
          <div className="bg-white border border-slate-200 w-full max-w-4xl rounded-[2.5rem] shadow-2xl my-8 overflow-hidden flex flex-col max-h-[90vh]">
            
            {/* Header */}
            <div className="p-6 bg-slate-900 text-white flex items-center justify-between border-b border-slate-800">
              <div>
                <div className="flex items-center gap-3">
                  <h2 className="text-xl font-black uppercase tracking-tight italic">
                    Order #{selectedOrder?.order_number || selectedOrder?.id?.slice(0, 8)}
                  </h2>
                  {selectedOrder && (
                    <span className={`px-3 py-1 rounded-full text-xs font-black uppercase tracking-wider border ${STATUS_COLORS[selectedOrder.status?.toLowerCase()]}`}>
                      {selectedOrder.status}
                    </span>
                  )}
                </div>
                <p className="text-slate-400 text-xs font-mono mt-1">
                  ORDER DATE: {selectedOrder?.created_at ? new Date(selectedOrder.created_at).toLocaleString('en-IN') : 'N/A'}
                </p>
              </div>

              <button
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 bg-slate-800 hover:bg-slate-700 text-slate-400 hover:text-white rounded-2xl flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Body */}
            {modalLoading ? (
              <div className="py-24 text-center space-y-3">
                <RefreshCw className="w-10 h-10 text-blue-500 animate-spin mx-auto" />
                <p className="text-slate-400 text-xs font-black uppercase tracking-widest">Fetching Fulfillment Telemetry...</p>
              </div>
            ) : selectedOrder ? (
              <div className="p-6 md:p-8 space-y-8 overflow-y-auto flex-1 font-sans">
                
                {/* Warehouse Dispatch Form */}
                <div className="bg-slate-50 border border-slate-200 rounded-3xl p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                      <Edit3 className="w-4 h-4 text-blue-600" /> WAREHOUSE DISPATCH CONTROLS
                    </span>
                    <button
                      onClick={handleSaveUpdate}
                      disabled={updating}
                      className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all shadow-md shadow-blue-600/20 flex items-center gap-2 active:scale-95"
                    >
                      {updating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      <span>Save Updates</span>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-2">
                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Order Status
                      </label>
                      <select
                        value={editStatus}
                        onChange={(e) => setEditStatus(e.target.value)}
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 outline-none focus:border-blue-500"
                      >
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="processing">Processing</option>
                        <option value="shipped">Shipped</option>
                        <option value="delivered">Delivered</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Courier Partner
                      </label>
                      <input
                        type="text"
                        value={editCourier}
                        onChange={(e) => setEditCourier(e.target.value)}
                        placeholder="e.g. Delhivery, Blue Dart, Xpressbees"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                        Tracking AWB #
                      </label>
                      <input
                        type="text"
                        value={editTracking}
                        onChange={(e) => setEditTracking(e.target.value)}
                        placeholder="AWB Tracking Number"
                        className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-bold text-slate-900 font-mono placeholder-slate-400 outline-none focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div className="space-y-1.5">
                    <label className="block text-[10px] font-black text-slate-500 uppercase tracking-wider">
                      Warehouse Log Notes
                    </label>
                    <textarea
                      rows={2}
                      value={editNotes}
                      onChange={(e) => setEditNotes(e.target.value)}
                      placeholder="Add dispatch notes or packaging logs..."
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-medium text-slate-900 placeholder-slate-400 outline-none focus:border-blue-500 resize-none"
                    />
                  </div>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-xs font-black text-slate-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4 text-slate-500" /> Items ({selectedOrder.items?.length || 0})
                      </h3>
                      <div className="border border-slate-200 rounded-2xl overflow-hidden divide-y divide-slate-100">
                        {selectedOrder.items && selectedOrder.items.length > 0 ? (
                          selectedOrder.items.map((item, idx) => (
                            <div key={idx} className="p-4 flex items-center gap-4 bg-white">
                              <div className="w-12 h-12 rounded-xl bg-slate-50 border border-slate-200 overflow-hidden shrink-0 flex items-center justify-center">
                                {item.product?.image_url ? (
                                  <img src={item.product.image_url} alt={item.product_name} className="w-full h-full object-contain" />
                                ) : (
                                  <Package className="w-5 h-5 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="font-black text-slate-900 text-xs uppercase truncate">{item.product_name || item.product?.name}</div>
                                {item.variant_name && <div className="text-[11px] text-slate-500 font-mono">Variant: {item.variant_name}</div>}
                                <div className="text-[11px] text-slate-400 font-mono">Qty: {item.quantity} × {formatINR(item.unit_price)}</div>
                              </div>
                              <div className="text-right font-black text-slate-900 text-xs font-mono">
                                {formatINR(item.total_price || (item.quantity * item.unit_price))}
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="p-4 text-xs text-slate-400 font-medium text-center">No item details recorded</div>
                        )}
                      </div>
                    </div>

                    <div className="bg-slate-50 rounded-2xl p-4 border border-slate-200 space-y-2 text-xs font-bold font-mono">
                      <div className="flex justify-between text-slate-600">
                        <span>Subtotal</span>
                        <span className="text-slate-900">{formatINR(selectedOrder.subtotal || selectedOrder.total_amount)}</span>
                      </div>
                      <div className="flex justify-between text-slate-600">
                        <span>Shipping</span>
                        <span className="text-slate-900">{formatINR(selectedOrder.shipping_charge || 0)}</span>
                      </div>
                      <div className="pt-2 border-t border-slate-200 flex justify-between text-sm font-black">
                        <span>Grand Total</span>
                        <span className="text-blue-600">{formatINR(selectedOrder.total_amount)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 flex items-center gap-2">
                        <User className="w-4 h-4 text-slate-500" /> Customer Profile
                      </h4>
                      <div className="text-xs font-medium space-y-1">
                        <div className="font-black text-slate-900 uppercase">{selectedOrder.customer?.name || selectedOrder.shipping_address?.full_name}</div>
                        {selectedOrder.customer?.email && <div className="text-slate-500 font-mono truncate">{selectedOrder.customer.email}</div>}
                        {selectedOrder.customer?.phone && <div className="text-slate-500 font-mono">Phone: {selectedOrder.customer.phone}</div>}
                      </div>
                    </div>

                    <div className="bg-white border border-slate-200 rounded-2xl p-4 space-y-2">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-wider border-b border-slate-100 pb-2 flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-slate-500" /> Shipping Destination
                      </h4>
                      {selectedOrder.shipping_address ? (
                        <div className="text-xs text-slate-600 space-y-1 font-medium leading-relaxed">
                          <div className="font-black text-slate-900 uppercase">{selectedOrder.shipping_address.full_name}</div>
                          <div>{selectedOrder.shipping_address.address_line1}</div>
                          {selectedOrder.shipping_address.address_line2 && <div>{selectedOrder.shipping_address.address_line2}</div>}
                          <div>{selectedOrder.shipping_address.city}, {selectedOrder.shipping_address.state} - {selectedOrder.shipping_address.pincode}</div>
                          <div className="pt-1 text-slate-500 font-bold font-mono">Phone: {selectedOrder.shipping_address.phone}</div>
                        </div>
                      ) : (
                        <div className="text-xs text-slate-400 font-medium">No shipping address recorded</div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
            ) : null}

          </div>
        </div>
      )}

    </div>
  );
};

export default ManagerOrders;

